import React, { useEffect, useState } from "react";

import { AlertTriangle } from "lucide-react";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import { Separator } from "~/components/ui/separator";
import { Textarea } from "~/components/ui/textarea";

import {
  AdministrativeCreditNoteInput,
  AdministrativeMode,
} from "./AdministrativeCreditNoteInput";
import { AdministrativeCreditItem } from "./AdministrativeCreditNoteItemSelector";
import {
  CreditNoteType,
  CreditNoteTypeSelector,
} from "./CreditNoteTypeSelector";
import {
  PartialCreditItem,
  PartialCreditNoteSelector,
} from "./PartialCreditNoteSelector";

interface DocumentDetail {
  id: number;
  lineNumber: number;
  quantity: number;
  netUnitValue: number;
  netAmount: number;
  variant: {
    description: string;
    code: string;
  };
}

interface EnhancedCreditNoteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (params: {
    motive: string;
    credit_note_type: CreditNoteType;
    partial_items?: PartialCreditItem[];
    administrative_amount?: number;
    administrative_reason?: string;
    administrative_mode?: AdministrativeMode;
    administrative_items?: AdministrativeCreditItem[];
    target_detail_index?: number;
  }) => Promise<void>;
  documentDetails: DocumentDetail[];
  isSubmitting?: boolean;
  documentNumber?: number;
  documentAmount?: number;
}

export const EnhancedCreditNoteDialog: React.FC<
  EnhancedCreditNoteDialogProps
> = ({
  isOpen,
  onClose,
  onSubmit,
  documentDetails,
  isSubmitting = false,
  documentNumber,
  documentAmount,
}) => {
  const [creditNoteType, setCreditNoteType] = useState<CreditNoteType>("full");
  const [motive, setMotive] = useState("");
  const [partialItems, setPartialItems] = useState<PartialCreditItem[]>([]);
  const [administrativeAmount, setAdministrativeAmount] = useState<number>(0);
  const [administrativeReason, setAdministrativeReason] = useState("");
  const [administrativeMode, setAdministrativeMode] =
    useState<AdministrativeMode>("fixed_amount");
  const [administrativeItems, setAdministrativeItems] = useState<
    AdministrativeCreditItem[]
  >([]);
  const [administrativeTargetDetailIndex, setAdministrativeTargetDetailIndex] =
    useState<number>(0);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setCreditNoteType("full");
      setMotive("");
      setPartialItems([]);
      setAdministrativeAmount(0);
      setAdministrativeReason("");
      setAdministrativeMode("fixed_amount");
      setAdministrativeItems([]);
      setAdministrativeTargetDetailIndex(0);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    const submitParams = {
      motive,
      credit_note_type: creditNoteType,
      ...(creditNoteType === "partial" && { partial_items: partialItems }),
      ...(creditNoteType === "administrative" && {
        administrative_amount: administrativeAmount,
        administrative_reason: administrativeReason,
        administrative_mode: administrativeMode,
        administrative_items: administrativeItems,
        target_detail_index: administrativeTargetDetailIndex,
      }),
    };

    await onSubmit(submitParams);
  };

  const isFormValid = (): boolean => {
    if (!motive.trim()) return false;

    switch (creditNoteType) {
      case "full":
        return true;
      case "partial":
        return partialItems.length > 0;
      case "administrative":
        if (administrativeMode === "fixed_amount") {
          return (
            administrativeAmount > 0 && administrativeReason.trim().length > 0
          );
        } else {
          return (
            administrativeItems.length > 0 &&
            administrativeReason.trim().length > 0
          );
        }
      default:
        return false;
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getEstimatedAmount = (): number => {
    switch (creditNoteType) {
      case "full":
        return documentAmount || 0;
      case "partial":
        return partialItems.reduce(
          (total, item) => total + item.quantity * (item.unit_value || 0),
          0
        );
      case "administrative":
        if (administrativeMode === "fixed_amount") {
          return administrativeAmount;
        } else {
          return administrativeItems.reduce((total, item) => {
            const detail = documentDetails.find(
              (d) => d.id === item.document_detail_id
            );
            if (!detail) return total;
            return total + item.adjustment_amount * detail.quantity;
          }, 0);
        }
      default:
        return 0;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] w-[95vw] max-w-4xl overflow-y-auto sm:w-full">
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl">
            Generar Nota de Crédito
          </DialogTitle>
          <DialogDescription className="text-sm">
            {documentNumber && (
              <span className="block break-words">
                Factura N° {documentNumber}
                {documentAmount &&
                  ` - Total: ${formatCurrency(documentAmount)}`}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 sm:space-y-6">
          {/* Credit Note Type Selection */}
          <CreditNoteTypeSelector
            selectedType={creditNoteType}
            onTypeChange={setCreditNoteType}
            disabled={isSubmitting}
          />

          {/* Type-specific inputs */}
          {creditNoteType === "partial" && (
            <PartialCreditNoteSelector
              documentDetails={documentDetails}
              selectedItems={partialItems}
              onItemsChange={setPartialItems}
              disabled={isSubmitting}
            />
          )}

          {creditNoteType === "administrative" && (
            <AdministrativeCreditNoteInput
              mode={administrativeMode}
              onModeChange={setAdministrativeMode}
              amount={administrativeAmount}
              onAmountChange={setAdministrativeAmount}
              reason={administrativeReason}
              onReasonChange={setAdministrativeReason}
              documentDetails={documentDetails}
              selectedItems={administrativeItems}
              onItemsChange={setAdministrativeItems}
              targetDetailIndex={administrativeTargetDetailIndex}
              onTargetDetailIndexChange={setAdministrativeTargetDetailIndex}
              disabled={isSubmitting}
            />
          )}

          <Separator />

          {/* Common motive field */}
          <div className="space-y-2">
            <Label htmlFor="credit-motive" className="text-sm sm:text-base">
              Motivo de la Nota de Crédito{" "}
              <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="credit-motive"
              value={motive}
              onChange={(e) => setMotive(e.target.value)}
              placeholder="Describe el motivo de la nota de crédito..."
              rows={3}
              disabled={isSubmitting}
              className="text-sm sm:text-base"
            />
            <p className="text-xs text-muted-foreground sm:text-sm">
              Este motivo aparecerá en el documento oficial de la nota de
              crédito.
            </p>
          </div>

          {/* Summary */}
          {isFormValid() && (
            <div className="rounded-lg border bg-muted/40 p-3 sm:p-4">
              <h4 className="mb-2 text-sm font-medium text-foreground sm:text-base">
                Resumen de la Nota de Crédito
              </h4>
              <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 sm:gap-4">
                <div className="flex flex-col space-y-1 sm:flex-row sm:space-y-0">
                  <span className="text-muted-foreground">Tipo:</span>
                  <span className="font-medium sm:ml-2">
                    {creditNoteType === "full" && "Anulación Completa"}
                    {creditNoteType === "partial" && "Nota de Crédito Parcial"}
                    {creditNoteType === "administrative" &&
                      `Nota de Crédito Administrativa ${administrativeMode === "fixed_amount" ? "(Monto Fijo)" : "(Ajustes por Producto)"}`}
                  </span>
                </div>
                <div className="flex flex-col space-y-1 sm:flex-row sm:space-y-0">
                  <span className="text-muted-foreground">Monto Estimado:</span>
                  <span className="font-medium sm:ml-2">
                    {formatCurrency(getEstimatedAmount())}
                  </span>
                </div>
                {creditNoteType === "partial" && (
                  <div className="flex flex-col space-y-1 sm:col-span-2 sm:flex-row sm:space-y-0">
                    <span className="text-muted-foreground">Productos:</span>
                    <span className="font-medium sm:ml-2">
                      {partialItems.length} producto(s) seleccionado(s)
                    </span>
                  </div>
                )}
                {creditNoteType === "administrative" &&
                  administrativeMode === "line_items" && (
                    <div className="flex flex-col space-y-1 sm:col-span-2 sm:flex-row sm:space-y-0">
                      <span className="text-muted-foreground">Ajustes:</span>
                      <span className="font-medium sm:ml-2">
                        {administrativeItems.length} producto(s) con ajustes
                      </span>
                    </div>
                  )}
              </div>
            </div>
          )}

          {/* Warning for partial/administrative */}
          {(creditNoteType === "partial" ||
            creditNoteType === "administrative") && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                {creditNoteType === "partial"
                  ? "La factura original permanecerá válida. Solo se creditarán los productos y cantidades seleccionadas."
                  : "La factura original permanecerá válida. Este es un crédito administrativo independiente."}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="flex-col space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            Cancelar
          </Button>
          <Button
            variant={creditNoteType === "full" ? "destructive" : "default"}
            onClick={handleSubmit}
            disabled={!isFormValid() || isSubmitting}
            className="w-full sm:w-auto"
          >
            {isSubmitting
              ? "Procesando..."
              : creditNoteType === "full"
                ? "Anular Factura"
                : creditNoteType === "partial"
                  ? "Generar NC Parcial"
                  : "Generar NC Administrativa"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
